import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import { seasonStatusTitle } from '@/lib/season/computedStatus';
import { useScienceSeasonLens } from '@/lib/science/seasonLens';

function formatRu(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Бейдж «📅 как на 31.12.2020» рядом с SeasonPicker.
 * Виден только когда активный сезон ≠ текущий.
 *
 * «Прошедший» считает общий вычислитель статуса сезона (канон v3 §5), а не
 * локальное сравнение `new Date(iso)` — оно трактует ISO-дату как UTC и в
 * последний день сезона вечером по МСК объявляло сезон прошедшим на сутки
 * раньше. Статус сезона словом добавлен в тултип.
 */
export function SeasonBadge() {
  const { season } = useActiveSeason();
  const science = useScienceSeasonLens();
  // AK-938: в «Науке» на «Все сезоны» сезон не один — бейдж «как на …» не к месту.
  if (science.active && science.lens === 'all') return null;
  if (!season || season.isCurrent) return null;

  const status = seasonStatusTitle({
    startsOn: season.startsOn,
    endsOn: season.endsOn,
    isCurrent: season.isCurrent,
  });
  const isPast = status === 'Завершён';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={isPast ? 'secondary' : 'primary'} className="m-0">
          📅 как на {formatRu(season.asOfDate)}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        Сезон {season.year}: {formatRu(season.startsOn)} —{' '}
        {formatRu(season.endsOn)}
        {status ? ` · ${status}` : ''}
      </TooltipContent>
    </Tooltip>
  );
}

export default SeasonBadge;
